import { is_plain_object, is_set, same_value } from '@/utilities/commons';

// Selections are keyed by filter_key: nodes carry no stable id across searches,
// and filters sharing a filter_key share the same request value.

export function parse_filters_metadata(filters, options = {}) {
  const categories = (filters && filters.categories) || {};
  const replace_nodes = options.replace_nodes || {};

  return Object.keys(categories).map((category_key) => ({
    key: category_key,
    ...normalize_category(
      categories[category_key] || {},
      category_key,
      category_key,
      replace_nodes
    ),
  }));
}

export function parse_suggestion(filters) {
  const suggestion = (filters && filters.suggestion) || {};

  return Object.keys(suggestion).map((key) => ({
    key,
    label: (suggestion[key] && suggestion[key].label) || null,
    values: (suggestion[key] && suggestion[key].values) || [],
  }));
}

export function flatten_nodes(categories) {
  return categories.flatMap((category) => category_filters(category));
}

// Every filter of a category, its sub_categories' included, in rendering order.
export function category_filters(category) {
  return category.filters.concat(
    category.sub_categories.flatMap((sub_category) =>
      category_filters(sub_category)
    )
  );
}

export function histogram_settings(node, bucket_transform) {
  const buckets = (node && node.buckets) || [];
  const transform = bucket_transform || default_bucket_transform;
  if (!buckets.length) return { min: 0, max: 0, values: [] };

  const values = buckets.map((bucket) => transform(bucket));
  const lower = values.find((value) => is_set(value.min));
  const upper = [...values].reverse().find((value) => is_set(value.max));

  return {
    min: lower ? lower.min : null,
    max: upper ? upper.max : null,
    values,
  };
}

export function group_nodes_in_panels(nodes, options = {}) {
  const merge_singles = options.merge_singles === true;
  const panels = [];

  nodes.forEach((node) => {
    const current = panels.length ? panels[panels.length - 1] : null;
    const previous = current ? current[current.length - 1] : null;
    const same_filter_key =
      previous !== null && previous.filter_key === node.filter_key;
    const singles_run =
      merge_singles &&
      previous !== null &&
      previous.selection_type === 'single' &&
      node.selection_type === 'single';

    if (same_filter_key || singles_run) current.push(node);
    else panels.push([node]);
  });

  return panels;
}

export function find_node(categories, filter_key) {
  return (
    flatten_nodes(categories).find((node) => node.filter_key === filter_key) ||
    null
  );
}

export function toggle_selection(selections, node, value) {
  const next = { ...selections };
  const key = node.filter_key;

  if (node.selection_type === 'single') {
    if (value) next[key] = node.value;
    else delete next[key];
    return next;
  }

  if (node.selection_type === 'choice') {
    if (value === null || value === undefined || same_value(next[key], value))
      delete next[key];
    else next[key] = value;
    return next;
  }

  if (node.selection_type === 'multiple') {
    const current = Array.isArray(next[key]) ? next[key] : [];
    const without = current.filter((item) => !same_value(item, value));
    const toggled =
      without.length === current.length ? current.concat([value]) : without;
    if (toggled.length) next[key] = toggled;
    else delete next[key];
    return next;
  }

  if (node.selection_type === 'histogram') {
    const merged = { ...(next[key] || {}), ...(value || {}) };
    const range = {};
    if (merged.minimum !== null && merged.minimum !== undefined)
      range.minimum = merged.minimum;
    if (merged.maximum !== null && merged.maximum !== undefined)
      range.maximum = merged.maximum;
    if (Object.keys(range).length) next[key] = range;
    else delete next[key];
    return next;
  }

  return next;
}

export function is_value_selected(selections, node, value) {
  const selection = selections[node.filter_key];

  if (node.selection_type === 'single') {
    return selection !== undefined && same_value(selection, node.value);
  }
  if (node.selection_type === 'choice') return same_value(selection, value);
  if (node.selection_type === 'multiple') {
    return (
      Array.isArray(selection) &&
      selection.some((item) => same_value(item, value))
    );
  }
  return false;
}

export function list_active_selections(nodes, selections) {
  const active = [];

  nodes.forEach((node) => {
    const selection = selections[node.filter_key];
    if (selection === undefined) return;

    if (node.selection_type === 'single') {
      if (!same_value(selection, node.value)) return;
      active.push({ node, value: node.value, label: node.label });
      return;
    }

    if (node.selection_type === 'choice') {
      active.push({
        node,
        value: selection,
        label: value_label(node, selection),
      });
      return;
    }

    if (node.selection_type === 'multiple') {
      if (!Array.isArray(selection)) return;
      (node.values || [])
        .filter((item) =>
          selection.some((value) => same_value(value, item.value))
        )
        .forEach((item) => {
          active.push({ node, value: item.value, label: item.label });
        });
      return;
    }

    if (node.selection_type === 'histogram') {
      active.push({ node, value: selection, label: node.label });
    }
  });

  return active;
}

export function build_filters_payload(nodes, selections, options = {}) {
  const value_transform = options.value_transform || ((node, value) => value);
  const payload = {};
  const seen = new Set();

  nodes.forEach((node) => {
    if (seen.has(node.filter_key)) return;
    seen.add(node.filter_key);

    const selection = selections[node.filter_key];
    if (selection === undefined) return;

    let value;

    if (node.selection_type === 'multiple') {
      if (!Array.isArray(selection)) return;
      const flat = selection.reduce(
        (accumulated, item) =>
          accumulated.concat(Array.isArray(item) ? item : [item]),
        []
      );
      value = [...new Set(flat)];
      if (!value.length) return;
    } else if (
      node.selection_type === 'single' ||
      node.selection_type === 'choice'
    ) {
      value = selection;
    } else if (node.selection_type === 'histogram') {
      value = { ...selection };
    } else {
      return;
    }

    const transformed = value_transform(node, value);
    if (transformed === undefined) return;
    set_filter_value(payload, node.filter_key, transformed);
  });

  return payload;
}

export function set_filter_value(payload, filter_key, value) {
  const path = String(filter_key).split('.');
  let target = payload;
  while (path.length > 1) {
    const key = path.shift();
    if (!is_plain_object(target[key])) target[key] = {};
    target = target[key];
  }
  target[path[0]] = value;
  return payload;
}

function default_bucket_transform(bucket) {
  return { min: bucket.from, max: bucket.to, count: bucket.count };
}

// `id` is positional: use it only as a render key, never as selection state.
// A sub_category with no filters at any depth is dropped.
function normalize_category(raw, category_key, id, replace_nodes) {
  const filters = (Array.isArray(raw.filters) ? raw.filters : [])
    .filter((filter) => filter)
    .map((filter, index) => {
      const replaced = replace_nodes[filter.filter_key];
      return normalize_node(
        category_key,
        `${id}-f${index}`,
        replaced !== undefined ? replaced : filter
      );
    });

  const sub_categories = (
    Array.isArray(raw.sub_categories) ? raw.sub_categories : []
  )
    .filter((sub_category) => sub_category)
    .map((sub_category, index) =>
      normalize_category(
        sub_category,
        category_key,
        `${id}-c${index}`,
        replace_nodes
      )
    )
    .filter((sub_category) => category_filters(sub_category).length);

  return {
    id,
    label: raw.label !== undefined ? raw.label : null,
    disclaimer: raw.disclaimer || null,
    filters,
    sub_categories,
  };
}

function normalize_node(category_key, id, raw) {
  const node = {
    id,
    category_key,
    label: raw.label !== undefined ? raw.label : null,
    filter_key: raw.filter_key,
    selection_type: raw.selection_type,
  };

  if (raw.tooltip !== undefined) node.tooltip = raw.tooltip;
  if (raw.value !== undefined) node.value = raw.value;
  if (raw.values !== undefined) node.values = raw.values;
  if (raw.buckets !== undefined) node.buckets = raw.buckets;

  return node;
}

function value_label(node, value) {
  const values = node.values || [];
  const match = values.find((item) => same_value(item.value, value));
  return match ? match.label : node.label;
}
